'use client'

import { useEffect, useRef, type ReactNode } from 'react'

import { cn } from '../cn'
import { FrameLink, useFramePath } from './config'

/**
 * One entry of the sidebar, in the design system's SidebarNav markup. Active by
 * prefix, with the path separator as the boundary, so /module/demo stays lit on
 * its subpages and /admin/users never lights up for /admin/users-import.
 *
 * On a short screen the list scrolls, and the active entry is brought into it:
 * otherwise a page near the end opens with its own entry out of sight. Only the
 * list's scroll position moves, never the page's.
 */
export function SidebarLink({
  href,
  icon,
  children,
  exact = false,
  document = false
}: {
  href: string
  /** Another app: a full page load (FrameNavItem.document). */
  document?: boolean
  icon?: ReactNode
  children: ReactNode
  /** Only the exact path counts — for the hub itself, which is a prefix of everything. */
  exact?: boolean
}) {
  const pathname = useFramePath()
  const active = pathname === href || (!exact && pathname.startsWith(`${href}/`))
  const ref = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const item = ref.current
    const list = item?.closest<HTMLElement>('.av-side__nav')
    if (!active || item == null || list == null) return
    // Keep the list's own padding around it, which is where the shadow sits.
    const pad = parseFloat(getComputedStyle(list).paddingTop) || 0
    const top = item.offsetTop - pad
    const bottom = item.offsetTop + item.offsetHeight + pad
    if (top < list.scrollTop) list.scrollTop = top
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight
  }, [active])

  return (
    <FrameLink
      ref={ref}
      href={href}
      document={document}
      aria-current={active ? 'page' : undefined}
      className={cn('av-side__item', active && 'is-active')}
    >
      {icon !== undefined && <span className="av-icon [&>svg]:size-[18px]">{icon}</span>}
      <span className="av-side__text">{children}</span>
    </FrameLink>
  )
}
